import React from 'react';
import PropTypes from 'prop-types';

const ScenarioLoadingContext = React.createContext(undefined);

// Actions
const FACET_PLAN_VIEW_LOADING_STARTED = 'facet-plan-view-loading-started';
const FACET_PLAN_VIEW_LOADING_COMPLETED = 'facet-plan-view-loading-completed';
const FACET_PLAN_CHART_DATA_LOADING_STARTED = 'facet-plan-chart-data-loading-started';
const FACET_PLAN_CHART_DATA_LOADING_SUCCEEDED = 'facet-plan-chart-data-loading-succeeded';
const FACET_PLAN_CHART_DATA_LOADING_FAILED = 'facet-plan-chart-data-loading-failed';

const init = () => {
  return {
    isFacetPlanViewLoading: false,
    isFacetPlanChartDataLoading: false,
    facetPlanChartDataLoadingError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case FACET_PLAN_VIEW_LOADING_STARTED: {
      return {
        ...state,
        isFacetPlanViewLoading: true,
        isFacetPlanChartDataLoading: true,
        facetPlanChartDataLoadingError: null,
      };
    }
    case FACET_PLAN_VIEW_LOADING_COMPLETED: {
      return {
        ...state,
        isFacetPlanViewLoading: false,
      };
    }
    case FACET_PLAN_CHART_DATA_LOADING_STARTED: {
      return {
        ...state,
        isFacetPlanChartDataLoading: true,
        facetPlanChartDataLoadingError: null,
      };
    }
    case FACET_PLAN_CHART_DATA_LOADING_SUCCEEDED: {
      return {
        ...state,
        isFacetPlanChartDataLoading: false,
        facetPlanChartDataLoadingError: null,
      };
    }
    case FACET_PLAN_CHART_DATA_LOADING_FAILED: {
      return {
        ...state,
        isFacetPlanChartDataLoading: false,
        facetPlanChartDataLoadingError: action.facetPlanChartDataLoadingError,
      };
    }
    default: {
      throw new Error(
        `Unhandled ScenarioChartContext action type: ${action.type}`,
      );
    }
  }
};

const ScenarioLoadingProvider = ({
  children,
  isPageContentLoading,
  pageContentLoadingError,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const setFacetPlanViewLoading = () => {
    dispatch({ type: FACET_PLAN_VIEW_LOADING_STARTED });
  };
  const setFacetPlanViewLoadingCompleted = () => {
    dispatch({ type: FACET_PLAN_VIEW_LOADING_COMPLETED });
  };
  const setFacetPlanChartDataLoading = () => {
    dispatch({ type: FACET_PLAN_CHART_DATA_LOADING_STARTED });
  };
  const setFacetPlanChartDataLoadingSucceeded = () => {
    dispatch({ type: FACET_PLAN_CHART_DATA_LOADING_SUCCEEDED });
  };
  const setFacetPlanChartDataLoadingFailed = (error) => {
    dispatch({ type: FACET_PLAN_CHART_DATA_LOADING_FAILED, facetPlanChartDataLoadingError: error });
  };

  React.useEffect(() => {
    if (isPageContentLoading) {
      setFacetPlanViewLoading();
      return;
    }
    setFacetPlanViewLoadingCompleted();
  }, [isPageContentLoading]);

  const memoizedValue = React.useMemo(() => {
    return {
      isFacetPlanViewLoading: state.isFacetPlanViewLoading,
      isFacetPlanChartDataLoading: state.isFacetPlanChartDataLoading,
      isScenarioLoading: state.isFacetPlanViewLoading || state.isFacetPlanChartDataLoading,
      hasLoadingError: state.facetPlanChartDataLoadingError !== null || pageContentLoadingError !== null,
      setFacetPlanViewLoading,
      setFacetPlanViewLoadingCompleted,
      setFacetPlanChartDataLoading,
      setFacetPlanChartDataLoadingSucceeded,
      setFacetPlanChartDataLoadingFailed,
    };
  }, [
    state,
    pageContentLoadingError,
    isPageContentLoading,
    setFacetPlanViewLoading,
    setFacetPlanViewLoadingCompleted,
    setFacetPlanChartDataLoading,
    setFacetPlanChartDataLoadingSucceeded,
    setFacetPlanChartDataLoadingFailed,
  ]);

  return (
    <ScenarioLoadingContext.Provider value={memoizedValue}>
      {children}
    </ScenarioLoadingContext.Provider>
  );
};

// Use Template State Context
const useScenarioLoadingContext = () => {
  const context = React.useContext(ScenarioLoadingContext);
  if (context === undefined) {
    throw new Error(
      'useScenarioLoadingContext must be used within a ScenarioLoadingProvider',
    );
  }
  return context;
};

ScenarioLoadingProvider.propTypes = {
  isPageContentLoading: PropTypes.bool.isRequired,
  pageContentLoadingError: PropTypes.instanceOf(Error).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export { ScenarioLoadingProvider, useScenarioLoadingContext };
